/**
 * Model drzewa „Eksplorator obiektów" — czysta funkcja ze snapshotu schematu.
 *
 * Hierarchia: baza → Schematy → schemat tenanta → gałęzie (Tabele, Widoki, …) → obiekty
 * → (dla tabel) Kolumny / Ograniczenia / Indeksy / Wyzwalacze. Filtr po nazwie obiektu
 * lub kolumny zostawia ścieżki do trafień i rozwija je automatycznie.
 */

import type { DbObjectRef, DbRelation, DbSchemaSnapshot } from './types';

export type DbTreeIcon =
  | 'database'
  | 'schema'
  | 'table'
  | 'view'
  | 'matview'
  | 'function'
  | 'procedure'
  | 'trigger_function'
  | 'sequence'
  | 'type'
  | 'column'
  | 'column_pk'
  | 'column_sensitive'
  | 'constraint'
  | 'index'
  | 'trigger';

export interface DbTreeNode {
  id: string;
  label: string;
  icon: DbTreeIcon;
  /** Liczba elementów gałęzi — pokazywana w nawiasie. */
  count?: number;
  /** Dopisek po prawej (typ kolumny, „PK"). */
  meta?: string;
  /** Obiekt, którego szczegóły otwiera klik. */
  ref?: DbObjectRef;
  /** Nazwa do wstawienia/skopiowania (tabela, kolumna, funkcja). */
  insertText?: string;
  children?: DbTreeNode[];
}

export type DbTreeGroupKey =
  | 'tables'
  | 'views'
  | 'matviews'
  | 'functions'
  | 'procedures'
  | 'triggerFunctions'
  | 'sequences'
  | 'types';

export interface DbTreeLabels {
  schemas: string;
  groups: Record<DbTreeGroupKey, string>;
  columns: string;
  constraints: string;
  indexes: string;
  triggers: string;
}

function relationChildren(r: DbRelation, labels: DbTreeLabels, prefix: string): DbTreeNode[] {
  const columns: DbTreeNode = {
    id: `${prefix}/columns`,
    label: labels.columns,
    icon: 'column',
    count: r.columns.length,
    children: r.columns.map((c) => ({
      id: `${prefix}/columns/${c.name}`,
      label: c.name,
      icon: c.sensitive ? 'column_sensitive' : c.isPrimaryKey ? 'column_pk' : 'column',
      meta: c.type,
      insertText: c.name,
    })),
  };
  if (r.kind !== 'table') return [columns];
  return [
    columns,
    {
      id: `${prefix}/constraints`,
      label: labels.constraints,
      icon: 'constraint',
      count: r.constraints.length,
      children: r.constraints.map((k) => ({
        id: `${prefix}/constraints/${k.name}`,
        label: k.name,
        icon: 'constraint',
        meta: { p: 'PK', f: 'FK', u: 'UNIQUE', c: 'CHECK' }[k.type],
      })),
    },
    {
      id: `${prefix}/indexes`,
      label: labels.indexes,
      icon: 'index',
      count: r.indexes.length,
      children: r.indexes.map((i) => ({
        id: `${prefix}/indexes/${i.name}`,
        label: i.name,
        icon: 'index',
        meta: i.unique ? 'unique' : undefined,
      })),
    },
    {
      id: `${prefix}/triggers`,
      label: labels.triggers,
      icon: 'trigger',
      count: r.triggers.length,
      children: r.triggers.map((t) => ({
        id: `${prefix}/triggers/${t.name}`,
        label: t.name,
        icon: 'trigger',
      })),
    },
  ];
}

export function buildDbTree(snapshot: DbSchemaSnapshot, labels: DbTreeLabels): DbTreeNode {
  const s = `db/schemas/${snapshot.schema}`;
  const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
  const relations = (kind: DbRelation['kind']) =>
    snapshot.relations.filter((r) => r.kind === kind).sort(byName);
  const relationNodes = (kind: DbRelation['kind'], group: string): DbTreeNode[] =>
    relations(kind).map((r) => ({
      id: `${s}/${group}/${r.name}`,
      label: r.name,
      icon: kind,
      ref: { kind, name: r.name },
      insertText: r.name,
      children: relationChildren(r, labels, `${s}/${group}/${r.name}`),
    }));
  const routineNodes = (kind: 'function' | 'procedure' | 'trigger_function', group: string) =>
    snapshot.routines
      .filter((f) => f.kind === kind)
      .sort(byName)
      .map((f) => ({
        id: `${s}/${group}/${f.name}(${f.args})`,
        label: `${f.name}(${f.args})`,
        icon: kind,
        meta: f.returns || undefined,
        ref: { kind, name: f.name, args: f.args },
        insertText: f.name,
      }));

  const group = (key: DbTreeGroupKey, icon: DbTreeIcon, children: DbTreeNode[]): DbTreeNode => ({
    id: `${s}/${key}`,
    label: labels.groups[key],
    icon,
    count: children.length,
    children,
  });

  const schemaNode: DbTreeNode = {
    id: s,
    label: snapshot.schema,
    icon: 'schema',
    children: [
      group('tables', 'table', relationNodes('table', 'tables')),
      group('views', 'view', relationNodes('view', 'views')),
      group('matviews', 'matview', relationNodes('matview', 'matviews')),
      group('functions', 'function', routineNodes('function', 'functions')),
      group('procedures', 'procedure', routineNodes('procedure', 'procedures')),
      group(
        'triggerFunctions',
        'trigger_function',
        routineNodes('trigger_function', 'triggerFunctions'),
      ),
      group(
        'sequences',
        'sequence',
        [...snapshot.sequences].sort().map((n) => ({
          id: `${s}/sequences/${n}`,
          label: n,
          icon: 'sequence' as const,
          insertText: n,
        })),
      ),
      group(
        'types',
        'type',
        [...snapshot.types]
          .sort()
          .map((n) => ({ id: `${s}/types/${n}`, label: n, icon: 'type' as const })),
      ),
    ],
  };

  return {
    id: 'db',
    label: `${snapshot.tenant.name} (${snapshot.tenant.code})`,
    icon: 'database',
    children: [
      { id: 'db/schemas', label: labels.schemas, icon: 'schema', count: 1, children: [schemaNode] },
    ],
  };
}

/** Domyślnie rozwinięte: baza, Schematy, schemat tenanta, Tabele. */
export function defaultExpanded(snapshot: DbSchemaSnapshot): Set<string> {
  const s = `db/schemas/${snapshot.schema}`;
  return new Set(['db', 'db/schemas', s, `${s}/tables`]);
}

/**
 * Filtr drzewa: zostawia węzły, których nazwa zawiera frazę, oraz ich przodków.
 * Zwraca przycięte drzewo i zbiór id do rozwinięcia (ścieżki do trafień).
 */
export function filterDbTree(
  root: DbTreeNode,
  query: string,
): { tree: DbTreeNode | null; expand: Set<string> } {
  const q = query.trim().toLowerCase();
  const expand = new Set<string>();
  if (!q) return { tree: root, expand };

  const walk = (node: DbTreeNode): DbTreeNode | null => {
    const selfHit = !node.count && node.label.toLowerCase().includes(q);
    const kids = (node.children ?? []).map(walk).filter((n): n is DbTreeNode => n !== null);
    if (kids.length) {
      expand.add(node.id);
      return { ...node, children: kids };
    }
    return selfHit ? { ...node, children: node.children } : null;
  };
  return { tree: walk(root), expand };
}

/** Słownik tabela → kolumny dla podpowiadacza edytora. */
export function completionSchema(snapshot: DbSchemaSnapshot): Record<string, string[]> {
  return Object.fromEntries(snapshot.relations.map((r) => [r.name, r.columns.map((c) => c.name)]));
}
